import _keyBy from 'lodash/keyBy';
import _map from 'lodash/map';
import _flatMap from 'lodash/flatMap';
import { classifySingleSkuBundleInstanceIds } from '../../utils/skuQuantity';

/**
 * Collect one sku per product, EXCEPT products sharing a BundleInstanceId whose members
 * all carry the identical sku (e.g. a Wall Rail + End Cap - Left/Right kit sold as one
 * packaged unit split into multiple placed images) — those count once, not once per
 * member. A BundleInstanceId group spanning DISTINCT skus (e.g. an inseparable soft
 * bundle: 2 brackets + 1 shelf, each its own real sku) must NOT collapse, or all but one
 * of its members would silently disappear — see classifySingleSkuBundleInstanceIds,
 * shared with getPlacedProductSkuAndQuantity's own placedProducts-side logic.
 */
const collectBundleSkus = (productList) => {
  const singleSkuBundleInstanceIds = classifySingleSkuBundleInstanceIds(
    productList.map(({ Sku, BundleInstanceId }) => ({ sku: Sku, bundleInstanceId: BundleInstanceId }))
  );

  const seenBundleInstanceIds = new Set();
  return productList.reduce((acc, { Sku, BundleInstanceId }) => {
    if (Sku == null) {
      return acc;
    }

    if (BundleInstanceId && singleSkuBundleInstanceIds.has(BundleInstanceId)) {
      if (seenBundleInstanceIds.has(BundleInstanceId)) {
        return acc;
      }
      seenBundleInstanceIds.add(BundleInstanceId);
    }

    acc.push(Sku);
    return acc;
  }, []);
};

export const designsTransformer = (response) => {
  const designs = _map(response, ({ DesignDocument }) => {
    let parsedDoc;
    try {
      parsedDoc = JSON.parse(DesignDocument);
    } catch (_parseError) {
      return null; // skip malformed entries
    }
    const {
      Id, Products, WallProducts, Stacks, ModularDesignType, Sku: designSku,
    } = parsedDoc;

    // Support WallStorage (WallProducts), Cabinet (Products),
    // and VehicleStorage (Stacks[].Products) formats.
    // Generic unified format uses Products[] directly (no nesting).
    let productList;
    let vehicleStacks = null; // preserved for VehicleStorage addStack flow
    if (Array.isArray(WallProducts) && WallProducts.length > 0) {
      productList = WallProducts;
    } else if (Array.isArray(Stacks) && Stacks.length > 0) {
      // VehicleStorage coordinate system: Row=0 is at the BOTTOM, increasing upward.
      // Convert to top-down (screen) coordinates by inverting:
      //   displayRow = stackHeight - productRow - productHeight
      // The GenericVisualizer FreeformCanvas uses `top: row` positioning.
      // NOTE: For products with floorOnly=true, getRecalculatedFreeformLayout overrides
      // the stored position with containerH - scaledHeight on reload. The inverted
      // coordinates ensure the placement position matches the floor-snap position,
      // keeping rack placement stable across page refreshes.
      vehicleStacks = Stacks;
      productList = _flatMap(Stacks, (stack) => {
        const stackHeight = Number(stack.Origin?.Height || 0);
        return (stack.Products || []).map((product) => {
          if (!stackHeight) return product;
          const originalRow = Number(product.Origin?.Row || 0);
          const productHeight = Number(product.Origin?.Height || 0);
          return {
            ...product,
            Origin: {
              ...product.Origin,
              Row: stackHeight - originalRow - productHeight,
            },
          };
        });
      });
    } else {
      const rawProducts = Array.isArray(Products) ? Products : [];
      // VehicleStorage unified Products[] format: invert floor-relative coords to
      // screen-top for FreeformCanvas. Build a virtual Stack (original rows)
      // for the VehicleStorage addStack / mapper.jsx flow.
      if (ModularDesignType === 'VehicleStorage' && rawProducts.length > 0) {
        const frameProduct = rawProducts.find((prod) => !prod.ParentIds || prod.Precedence === 1);
        const stackHeight = Number(frameProduct?.Origin?.Height || 0);
        productList = stackHeight > 0
          ? rawProducts.map((product) => {
            const originalRow = Number(product.Origin?.Row || 0);
            const productHeight = Number(product.Origin?.Height || 0);
            return {
              ...product,
              Origin: {
                ...product.Origin,
                Row: stackHeight - originalRow - productHeight,
              },
            };
          })
          : rawProducts;
        vehicleStacks = [{
          StackId: Id,
          Sku: designSku ?? null,
          Name: '',
          Origin: frameProduct?.Origin ?? { Row: 0, Column: 0, Height: 0, Width: 0 },
          Products: rawProducts,
        }];
      } else {
        productList = rawProducts;
      }
    }
    return {
      id: Id,
      modularDesignType: ModularDesignType || null,
      // Preserve original Stacks for the VehicleStorage visualizer addStack flow.
      ...(vehicleStacks && { Stacks: vehicleStacks }),
      // Design-level SKU is present when the bundle is purchased as a single unit.
      // When set it becomes the authoritative bundleSkus entry; individual products
      // will carry Sku: null in that scenario.
      sku: designSku ?? null,
      products: _map(
        productList,
        ({
          InstanceId,
          BundleInstanceId,
          Sku,
          ChoiceId,
          Origin,
          Type,
          ParentIds,
          ParentId,
          ChildrenIds,
          Attachment,
          Precedence,
          Name,
        }) => ({
          instanceId: InstanceId || null,
          // Groups Frame-type members of the same physical assembly (e.g. Wall Rail +
          // End Cap - Left/Right) that were placed together as one bundle — without
          // this, bundleGroup.utils's resolveBundleGroupMembers/cascading-removal have
          // no way to tell they belong together once this design is reloaded.
          bundleInstanceId: BundleInstanceId || null,
          sku: Sku,
          choiceId: ChoiceId,
          origin: Origin,
          type: Type || null,
          // VehicleStorage uses singular ParentId; WallStorage uses ParentIds array
          parentIds: (() => {
            if (Array.isArray(ParentIds)) return ParentIds;
            if (ParentId) return [ParentId];
            return [];
          })(),
          childrenIds: Array.isArray(ChildrenIds) ? ChildrenIds : [],
          attachment: Attachment || null,
          precedence: Precedence || 0,
          name: Name || '',
        })),
      bundleChoices: _map(productList, ({ ChoiceId }) => ChoiceId),
      // If a design-level Sku exists use it; otherwise collect non-null product SKUs
      // (collapsing same-sku BundleInstanceId groups to one entry — see collectBundleSkus).
      bundleSkus: designSku != null
        ? [designSku]
        : collectBundleSkus(productList)
    };
  }).filter(Boolean);
  const normalized = _keyBy(designs, 'id');

  return normalized;
};

export const cabinetdesignsTransformer = (response) => {
  if (!Array.isArray(response)) {
    return response; // Handle cases where the response is not an array
  }

  return response.map((design) => ({
    ...design,
    Matrix: {
      ...design.Matrix,
      Rows: (design.Matrix.Rows / 100) ?? 0,
      Columns: (design.Matrix.Columns / 100) ?? 0,
    },
    Products: (design.Products ?? []).map((product) => {
      if (product && product.Origin) {
        return {
          ...product,
          Origin: {
            Row: product.Origin.Row / 100,
            Column: product.Origin.Column / 100,
            Height: product.Origin.Height / 100,
            Width: product.Origin.Width / 100,
          },
        };
      }
      return product; // Return product unchanged if Origin is missing
    }),
    Cart: (design.Cart ?? []),
  }));
};

export const documentTransformerByDesignType = (response, withConfiguratorBundleData) => {
  const documents = _map(response, (document) => {
    const { ModularDesignType } = document;

    if (ModularDesignType === 'VehicleStorage') {
      // New unified format (flat Products[]) has no Stacks[].
      // Build a virtual Stack from the Frame product so the existing render
      // pipeline (addStack → VisualizedStacks) works without any changes.
      const hasStacks = Array.isArray(document.Stacks) && document.Stacks.length > 0;
      const resolvedStacks = hasStacks
        ? document.Stacks
        : (() => {
          const products = Array.isArray(document.Products) ? document.Products : [];
          if (!products.length) return [];
          const frameProduct = products.find((prod) => !prod.ParentIds || prod.Precedence === 1);
          return [{
            StackId: document.Id,
            Sku: document.Sku ?? null,
            Name: document.DesignName ?? '',
            Origin: frameProduct?.Origin ?? { Row: 0, Column: 0, Height: 0, Width: 0 },
            Products: products,
          }];
        })();

      const stackProducts = _flatMap(resolvedStacks, (stack) => stack.Products);

      if (withConfiguratorBundleData) {
        const stacksWithSku = resolvedStacks.filter((stack) => stack.Sku);
        const bundleSkus = stacksWithSku.length > 0
          ? _map(stacksWithSku, ({ Sku }) => Sku)
          : _flatMap(stackProducts, ({ Sku }) => (Sku ? [Sku] : []));
        return {
          id: document.Id,
          bundleChoices: _map(stackProducts, ({ ChoiceId }) => ChoiceId),
          bundleSkus,
          ...document,
          Stacks: resolvedStacks,
        };
      }
      return {
        ...document,
        Stacks: resolvedStacks,
      };
    }

    if (ModularDesignType === 'WallStorage') {
      return {
        ...document,
      };
    }

    return {
      Id: document.Id
    };
  });

  return _keyBy(documents, 'Id');
};
